import { clamp } from '../utils/math';

export class InputManager {
  /** Aim X in world units (smoothed) */
  aimX = 0;
  /** Raw aim target */
  private targetAimX = 0;
  private halfWidth = 4;
  private dropRequested = false;
  private pauseRequested = false;
  private enabled = true;
  private canvas: HTMLCanvasElement;
  private keys = new Set<string>();
  private pointerDown = false;
  private gamepadIndex: number | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.bind();
  }

  setBounds(halfWidth: number): void {
    this.halfWidth = halfWidth;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  private bind(): void {
    const c = this.canvas;

    c.addEventListener('pointerdown', (e) => {
      if (!this.enabled) return;
      this.pointerDown = true;
      c.setPointerCapture(e.pointerId);
      this.updateFromClient(e.clientX);
    });
    c.addEventListener('pointermove', (e) => {
      if (!this.enabled) return;
      this.updateFromClient(e.clientX);
    });
    c.addEventListener('pointerup', (e) => {
      if (!this.enabled) return;
      this.updateFromClient(e.clientX);
      if (this.pointerDown) {
        this.dropRequested = true;
      }
      this.pointerDown = false;
    });
    c.addEventListener('pointercancel', () => {
      this.pointerDown = false;
    });

    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (!this.enabled) return;
      if (e.code === 'Space' || e.code === 'ArrowDown' || e.code === 'KeyS') {
        e.preventDefault();
        this.dropRequested = true;
      }
      if (e.code === 'Escape' || e.code === 'KeyP') {
        this.pauseRequested = true;
      }
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });

    window.addEventListener('gamepadconnected', (e) => {
      this.gamepadIndex = e.gamepad.index;
    });
  }

  private updateFromClient(clientX: number): void {
    const rect = this.canvas.getBoundingClientRect();
    // Map pointer across the central ~70% of the canvas to the full jar width
    // so aiming feels precise and matches the framed playfield.
    const u = (clientX - rect.left) / rect.width;
    const mapped = (u - 0.5) * 2; // -1..1
    const scale = 1.15; // slight overscan then clamp
    this.targetAimX = clamp(mapped * this.halfWidth * scale, -this.halfWidth, this.halfWidth);
  }

  update(dt: number, worldFromNdc?: (ndcX: number) => number): void {
    // Keyboard aim
    const speed = 7;
    if (this.keys.has('ArrowLeft') || this.keys.has('KeyA')) {
      this.targetAimX -= speed * dt;
    }
    if (this.keys.has('ArrowRight') || this.keys.has('KeyD')) {
      this.targetAimX += speed * dt;
    }

    // Gamepad
    if (this.gamepadIndex !== null) {
      const gps = navigator.getGamepads();
      const gp = gps[this.gamepadIndex];
      if (gp) {
        const axis = gp.axes[0] ?? 0;
        if (Math.abs(axis) > 0.15) {
          this.targetAimX += axis * speed * 1.4 * dt;
        }
        // A / X button edge would need latching; simple held drop once per press via buttons[0]
        if (gp.buttons[0]?.pressed) {
          // handled with latch
          if (!(gp as unknown as { _dropLatch?: boolean })._dropLatch) {
            this.dropRequested = true;
            (gp as unknown as { _dropLatch?: boolean })._dropLatch = true;
          }
        } else {
          (gp as unknown as { _dropLatch?: boolean })._dropLatch = false;
        }
        if (gp.buttons[9]?.pressed) this.pauseRequested = true;
      }
    }

    this.targetAimX = clamp(this.targetAimX, -this.halfWidth + 0.15, this.halfWidth - 0.15);
    // Smooth aim
    const k = 1 - Math.pow(0.0008, dt);
    this.aimX = this.aimX + (this.targetAimX - this.aimX) * k;

    void worldFromNdc;
  }

  consumeDrop(): boolean {
    if (this.dropRequested) {
      this.dropRequested = false;
      return true;
    }
    return false;
  }

  consumePause(): boolean {
    if (this.pauseRequested) {
      this.pauseRequested = false;
      return true;
    }
    return false;
  }

  /** Set aim directly (for external UI) */
  setAimX(x: number): void {
    this.targetAimX = clamp(x, -this.halfWidth + 0.15, this.halfWidth - 0.15);
  }
}
